import { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Chip,
} from "@material-ui/core";
import {
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
} from "@material-ui/icons";

const CommentInput = ({ onSubmit, isLoading = false }) => {
  const [content, setContent] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);

  const canSend = content.trim() || pendingFiles.length > 0;

  const handleSubmit = () => {
    if (!canSend) return;
    onSubmit({ content: content.trim(), isInternal, files: pendingFiles });
    setContent("");
    setPendingFiles([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    }
    // eslint-disable-next-line no-param-reassign
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "20px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Box sx={{ display: "flex", gap: "4px", mb: 2 }}>
        <Button
          variant={!isInternal ? "contained" : "text"}
          size="small"
          onClick={() => setIsInternal(false)}
          sx={{
            borderRadius: "6px",
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            ...(
              !isInternal
                ? { backgroundColor: "#0369A1", color: "#fff", "&:hover": { backgroundColor: "#075985" } }
                : { color: "#64748B" }
            ),
          }}
        >
          Reply to Customer
        </Button>
        <Button
          variant={isInternal ? "contained" : "text"}
          size="small"
          onClick={() => setIsInternal(true)}
          sx={{
            borderRadius: "6px",
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            ...(
              isInternal
                ? { backgroundColor: "#D97706", color: "#fff", "&:hover": { backgroundColor: "#B45309" } }
                : { color: "#64748B" }
            ),
          }}
        >
          Internal Note
        </Button>
      </Box>
      <TextField
        multiline
        minRows={4}
        fullWidth
        placeholder={isInternal ? "Write an internal note..." : "Write a reply..."}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            fontSize: 14,
            lineHeight: 1.6,
            ...(isInternal && {
              "& fieldset": { borderColor: "#D97706" },
              backgroundColor: "#FFFBEB",
            }),
          },
        }}
      />
      {pendingFiles.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: 1.5, py: 0.5 }}>
          {pendingFiles.map((file, index) => {
            const isImage = file.type.startsWith("image/");
            return (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  position: "relative",
                  width: 72,
                  height: 72,
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                {isImage ? (
                  <Box
                    component="img"
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <>
                    <DescriptionIcon sx={{ color: "#64748B", fontSize: 28 }} />
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "#64748B",
                        textAlign: "center",
                        px: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {file.name}
                    </Typography>
                  </>
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: 18,
                    height: 18,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                    p: 0,
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <CloseIcon style={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            sx={{ color: "#64748B" }}
          >
            <AttachFileIcon style={{ fontSize: 18 }} />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={!canSend || isLoading}
          startIcon={<SendIcon style={{ fontSize: 16 }} />}
          sx={{
            backgroundColor: isInternal ? "#D97706" : "#0369A1",
            color: "#fff",
            textTransform: "none",
            borderRadius: "6px",
            fontSize: 13,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: isInternal ? "#B45309" : "#075985",
            },
          }}
        >
          {isInternal ? "Add Note" : "Send Reply"}
        </Button>
      </Box>
    </Box>
  );
};

CommentInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default CommentInput;
